import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../../api/axios'
import { Alert } from '../../../components/ui/Alert'
import { Modal } from '../../../components/ui/Modal'
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog'
import { FormField, inputClass } from '../../../components/ui/FormField'
import { nomePf } from '../divisoes'
import { baixarPdf, btnPrimario, btnSecundario, cartao, erroApi } from './comum'

// Squads da etapa IPSC: criar, distribuir os inscritos automaticamente, mover entre squads e imprimir

export interface InscritoIpsc {
  idinscricao: number
  numero_inscricao: number
  atirador: string
  clube: string | null
  divisao: string | null
  power_factor: string | null
  idsquad: number | null
  squad_numero: number | null
}
interface Squad {
  idsquad: number
  numero: number
  nome: string | null
  horario: string | null
  limite: number | null
  observacao: string | null
  atiradores: InscritoIpsc[]
}
interface Dados { squads: Squad[]; sem_squad: InscritoIpsc[] }
type FormSquad = { numero: string; nome: string; horario: string; limite: string }

export function useSquads(idetapa: number) {
  return useQuery({
    queryKey: ['ipsc-squads', idetapa],
    queryFn: () => api.get<{ data: Dados }>(`/ipsc/etapas/${idetapa}/squads`).then((r) => r.data.data),
  })
}

export function AbaSquads({ idetapa }: { idetapa: number }) {
  const qc = useQueryClient()
  const { data } = useSquads(idetapa)
  const squads = data?.squads ?? []
  const semSquad = data?.sem_squad ?? []
  const [editando, setEditando] = useState<Squad | 'novo' | null>(null)
  const [form, setForm] = useState<FormSquad>({ numero: '1', nome: '', horario: '', limite: '' })
  const [excluir, setExcluir] = useState<Squad | null>(null)
  const [distribuir, setDistribuir] = useState(false)
  const [dist, setDist] = useState({ limite: '10', manter_existentes: true, criar_squads: true })
  const [alerta, setAlerta] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const atualizar = () => {
    qc.invalidateQueries({ queryKey: ['ipsc-squads', idetapa] })
    qc.invalidateQueries({ queryKey: ['ipsc-etapas'] })
  }
  const falhou = (padrao: string) => (e: unknown) => setAlerta({ type: 'error', message: erroApi(e, padrao) })

  const salvar = useMutation({
    mutationFn: (d: object) => (editando && editando !== 'novo' ? api.put(`/ipsc/squads/${editando.idsquad}`, d) : api.post(`/ipsc/etapas/${idetapa}/squads`, d)),
    onSuccess: () => { atualizar(); setEditando(null); setAlerta({ type: 'success', message: 'Squad salvo' }) },
    onError: falhou('Erro ao salvar o squad'),
  })
  const apagar = useMutation({
    mutationFn: (s: Squad) => api.delete(`/ipsc/squads/${s.idsquad}`),
    onSuccess: () => { atualizar(); setAlerta({ type: 'success', message: 'Squad excluído (os atiradores ficaram sem squad)' }) },
    onError: falhou('Erro ao excluir o squad'),
    onSettled: () => setExcluir(null),
  })
  const distribuirM = useMutation({
    mutationFn: () => api.post<{ data: { distribuidos: number; squads_criados: number; sem_lugar: number } }>(`/ipsc/etapas/${idetapa}/squads/distribuir`, {
      limite: Number(dist.limite) || 10, manter_existentes: dist.manter_existentes, criar_squads: dist.criar_squads,
    }),
    onSuccess: (r) => {
      atualizar()
      setDistribuir(false)
      const x = r.data.data
      setAlerta({ type: x.sem_lugar ? 'error' : 'success', message: `${x.distribuidos} atirador(es) distribuído(s), ${x.squads_criados} squad(s) criado(s)${x.sem_lugar ? `; ${x.sem_lugar} ficaram sem lugar (aumente o limite ou crie squads)` : ''}` })
    },
    onError: falhou('Erro ao distribuir'),
  })
  // mover: tira do squad de origem e poe no de destino (no fim)
  const mover = useMutation({
    mutationFn: async ({ insc, destino }: { insc: InscritoIpsc; destino: number | null }) => {
      // ids chegam como texto (bigint): compara como texto
      const origem = squads.find((s) => String(s.idsquad) === String(insc.idsquad))
      if (destino) {
        const alvo = squads.find((s) => String(s.idsquad) === String(destino))!
        await api.put(`/ipsc/squads/${destino}/atiradores`, { idinscricoes: [...alvo.atiradores.map((a) => a.idinscricao), insc.idinscricao] })
      } else if (origem) {
        await api.put(`/ipsc/squads/${origem.idsquad}/atiradores`, { idinscricoes: origem.atiradores.filter((a) => a.idinscricao !== insc.idinscricao).map((a) => a.idinscricao) })
      }
    },
    onSuccess: atualizar,
    onError: falhou('Erro ao mover o atirador'),
  })

  function abrir(s: Squad | 'novo') {
    setEditando(s)
    setForm(s === 'novo'
      ? { numero: String((squads.at(-1)?.numero ?? 0) + 1), nome: '', horario: '', limite: '' }
      : { numero: String(s.numero), nome: s.nome ?? '', horario: s.horario ?? '', limite: s.limite ? String(s.limite) : '' })
  }

  const linha = (a: InscritoIpsc, i: number) => (
    <li key={a.idinscricao} className="flex items-center gap-2 py-1.5 text-sm" data-inscricao={a.idinscricao}>
      <span className="w-5 text-right text-compumaq-textoSuave">{i + 1}</span>
      <span className="flex-1 min-w-0">
        <span className="text-compumaq-texto">{a.atirador}</span>
        <span className="block text-xs text-compumaq-textoSuave">{a.divisao ?? 'sem divisão'} {nomePf(a.power_factor)}{a.clube ? ` · ${a.clube}` : ''}</span>
      </span>
      <select aria-label={`Mover ${a.atirador}`} className="text-xs bg-compumaq-preto border border-compumaq-dourado/25 rounded px-1 py-1 text-compumaq-texto"
        value={a.idsquad ?? ''} onChange={(e) => mover.mutate({ insc: a, destino: e.target.value ? Number(e.target.value) : null })}>
        <option value="">Sem squad</option>
        {squads.map((s) => <option key={s.idsquad} value={s.idsquad}>Squad {s.numero}</option>)}
      </select>
    </li>
  )

  return (
    <div className="space-y-3">
      {alerta && <Alert type={alerta.type} message={alerta.message} onClose={() => setAlerta(null)} />}
      <div className="flex flex-wrap gap-2 justify-end">
        <button className={btnSecundario} onClick={() => baixarPdf(`/ipsc/etapas/${idetapa}/squads/pdf`, 'squads.pdf').catch(falhou('Erro ao gerar o PDF'))}>Imprimir / PDF</button>
        <button className={btnSecundario} onClick={() => setDistribuir(true)}>Distribuir automaticamente</button>
        <button className={btnPrimario} onClick={() => abrir('novo')}>+ Novo squad</button>
      </div>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {squads.map((s) => (
          <div key={s.idsquad} className={`${cartao} p-3`} data-squad={s.numero}>
            <div className="flex items-start justify-between gap-2 mb-1">
              <div>
                <p className="font-semibold text-compumaq-texto">Squad {s.numero}{s.nome ? ` — ${s.nome}` : ''}</p>
                <p className="text-xs text-compumaq-textoSuave">{s.horario ? `${s.horario} · ` : ''}{s.atiradores.length}{s.limite ? `/${s.limite}` : ''} atirador(es)</p>
              </div>
              <div className="whitespace-nowrap">
                <button className="text-xs text-compumaq-doradoClaro font-medium mr-2" onClick={() => abrir(s)}>Editar</button>
                <button className="text-xs text-red-500 font-medium" onClick={() => setExcluir(s)}>Excluir</button>
              </div>
            </div>
            {s.atiradores.length ? <ul className="divide-y divide-compumaq-dourado/15">{s.atiradores.map(linha)}</ul> : <p className="text-xs text-compumaq-textoSuave py-2">Vazio</p>}
          </div>
        ))}
        <div className={`${cartao} p-3 border-dashed`} data-squad="sem">
          <p className="font-semibold text-compumaq-texto mb-1">Sem squad ({semSquad.length})</p>
          {semSquad.length ? <ul className="divide-y divide-compumaq-dourado/15">{semSquad.map(linha)}</ul> : <p className="text-xs text-compumaq-textoSuave py-2">Todos os inscritos estão em algum squad.</p>}
        </div>
      </div>

      <Modal open={editando !== null} onClose={() => setEditando(null)} title={editando === 'novo' ? 'Novo squad' : 'Editar squad'}>
        <form className="space-y-3" onSubmit={(e) => {
          e.preventDefault()
          salvar.mutate({ numero: Number(form.numero), nome: form.nome || null, horario: form.horario || null, limite: form.limite ? Number(form.limite) : null })
        }}>
          <div className="grid grid-cols-3 gap-3">
            <FormField label="Nº" required><input className={inputClass} type="number" min={1} value={form.numero} onChange={(e) => setForm({ ...form, numero: e.target.value })} aria-label="Número do squad" /></FormField>
            <div className="col-span-2"><FormField label="Nome"><input className={inputClass} value={form.nome} placeholder="Ex.: Manhã" onChange={(e) => setForm({ ...form, nome: e.target.value })} /></FormField></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Horário"><input className={inputClass} value={form.horario} placeholder="Ex.: 08:00" onChange={(e) => setForm({ ...form, horario: e.target.value })} /></FormField>
            <FormField label="Limite de atiradores" hint="Vazio = sem limite"><input className={inputClass} type="number" min={1} value={form.limite} onChange={(e) => setForm({ ...form, limite: e.target.value })} aria-label="Limite de atiradores" /></FormField>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className={btnSecundario} onClick={() => setEditando(null)}>Cancelar</button>
            <button type="submit" className={btnPrimario} disabled={salvar.isPending}>{salvar.isPending ? 'Salvando...' : 'Salvar'}</button>
          </div>
        </form>
      </Modal>

      <Modal open={distribuir} onClose={() => setDistribuir(false)} title="Distribuir automaticamente">
        <div className="space-y-3">
          <p className="text-sm text-compumaq-textoSuave">
            Coloca os inscritos nos squads em ordem de divisão (a mesma divisão fica junta), respeitando o limite de cada squad.
          </p>
          <FormField label="Limite por squad" hint="Usado nos squads sem limite e nos que forem criados">
            <input className={inputClass} type="number" min={1} value={dist.limite} onChange={(e) => setDist({ ...dist, limite: e.target.value })} aria-label="Limite por squad" />
          </FormField>
          <label className="flex items-center gap-2 text-sm text-compumaq-texto">
            <input type="checkbox" checked={dist.manter_existentes} onChange={(e) => setDist({ ...dist, manter_existentes: e.target.checked })} aria-label="Manter quem já está em squad" />
            Manter quem já está em squad (distribui só os que faltam)
          </label>
          <label className="flex items-center gap-2 text-sm text-compumaq-texto">
            <input type="checkbox" checked={dist.criar_squads} onChange={(e) => setDist({ ...dist, criar_squads: e.target.checked })} />
            Criar squads novos quando os existentes encherem
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button className={btnSecundario} onClick={() => setDistribuir(false)}>Cancelar</button>
            <button className={btnPrimario} disabled={distribuirM.isPending} onClick={() => distribuirM.mutate()}>{distribuirM.isPending ? 'Distribuindo...' : 'Distribuir'}</button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!excluir}
        title="Excluir squad"
        message={`Excluir o squad ${excluir?.numero}? Os ${excluir?.atiradores.length ?? 0} atirador(es) dele ficam sem squad.`}
        confirmLabel="Excluir"
        loading={apagar.isPending}
        onConfirm={() => excluir && apagar.mutate(excluir)}
        onClose={() => setExcluir(null)}
      />
    </div>
  )
}
